import { AUTH_PATHS, AUTH_SESSION_KEYS } from '../constants';

const POST_LOGIN_REDIRECT_TTL_MS = 30 * 60 * 1000;

type StoredPostLoginRedirect = {
  path: string;
  savedAt: number;
};

const AUTH_ROUTE_PATHNAMES = new Set<string>(
  Object.values(AUTH_PATHS).filter((path) => path !== AUTH_PATHS.HOME),
);

export const isOAuthCallbackUrl = (href: string): boolean => {
  try {
    const url = new URL(href);
    return (
      url.searchParams.has('state') &&
      (url.searchParams.has('code') || url.searchParams.has('error'))
    );
  } catch {
    return false;
  }
};

export const sanitizeInternalPath = (
  candidate: string | null | undefined,
): string => {
  if (typeof candidate !== 'string') {
    return AUTH_PATHS.HOME;
  }

  const trimmed = candidate.trim();
  if (
    trimmed.length === 0 ||
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    trimmed.includes('\\')
  ) {
    return AUTH_PATHS.HOME;
  }

  let url: URL;
  try {
    url = new URL(trimmed, 'https://app.local');
  } catch {
    return AUTH_PATHS.HOME;
  }

  if (url.origin !== 'https://app.local') {
    return AUTH_PATHS.HOME;
  }

  if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) {
    return AUTH_PATHS.HOME;
  }

  if (AUTH_ROUTE_PATHNAMES.has(url.pathname)) {
    return AUTH_PATHS.HOME;
  }

  return `${url.pathname}${url.search}`;
};

export const savePostLoginRedirect = (pathWithSearch: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (isOAuthCallbackUrl(window.location.href)) {
    return;
  }

  const sanitized = sanitizeInternalPath(pathWithSearch);
  if (sanitized === AUTH_PATHS.HOME) {
    return;
  }

  const payload: StoredPostLoginRedirect = {
    path: sanitized,
    savedAt: Date.now(),
  };
  sessionStorage.setItem(
    AUTH_SESSION_KEYS.POST_LOGIN_REDIRECT,
    JSON.stringify(payload),
  );
};

export const readPostLoginRedirect = (): string => {
  if (typeof window === 'undefined') {
    return AUTH_PATHS.HOME;
  }

  const raw = sessionStorage.getItem(AUTH_SESSION_KEYS.POST_LOGIN_REDIRECT);
  if (!raw) {
    return AUTH_PATHS.HOME;
  }

  try {
    const parsed = JSON.parse(raw) as StoredPostLoginRedirect;
    if (
      typeof parsed.path !== 'string' ||
      typeof parsed.savedAt !== 'number' ||
      !Number.isFinite(parsed.savedAt)
    ) {
      return AUTH_PATHS.HOME;
    }

    if (Date.now() - parsed.savedAt > POST_LOGIN_REDIRECT_TTL_MS) {
      return AUTH_PATHS.HOME;
    }

    return sanitizeInternalPath(parsed.path);
  } catch {
    return AUTH_PATHS.HOME;
  }
};

export const clearPostLoginRedirect = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(AUTH_SESSION_KEYS.POST_LOGIN_REDIRECT);
};
